"use client";

import { useSyncExternalStore } from "react";
import { CONSENT_MAX_AGE_MS, CONSENT_VERSION, type Consent, type OptionalCategory } from "@/lib/consent";

const KEY = "gcg-consent";
const CHANGE = "gcg-consent-change";
const OPEN = "gcg-consent-open";

let cache: { raw: string | null; value: Consent | null } = { raw: null, value: null };

function read(): Consent | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (raw === cache.raw) return cache.value;
  let value: Consent | null = null;
  try {
    const v = raw ? (JSON.parse(raw) as Consent) : null;
    const fresh = v && v.v === CONSENT_VERSION && Date.now() - Date.parse(v.at) < CONSENT_MAX_AGE_MS;
    value = fresh ? { v: v.v, at: v.at, statistics: v.statistics === true, media: v.media === true, marketing: v.marketing === true } : null;
  } catch {
    value = null;
  }
  cache = { raw, value };
  return value;
}

export function saveConsent(choice: Record<OptionalCategory, boolean>) {
  const value: Consent = { v: CONSENT_VERSION, at: new Date().toISOString(), ...choice };
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Privater Modus ohne Speicher: Auswahl gilt nur bis zum Neuladen.
    cache = { raw: "__memory__", value };
  }
  window.dispatchEvent(new Event(CHANGE));
}

function subscribe(cb: () => void) {
  const onStorage = (e: StorageEvent) => e.key === KEY && cb();
  window.addEventListener(CHANGE, cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE, cb);
    window.removeEventListener("storage", onStorage);
  };
}

const UNKNOWN = "unknown" as const;

/** Gespeicherte Einwilligung; auf dem Server und vor dem Hydrieren "unknown". */
export function useConsent(): Consent | null | typeof UNKNOWN {
  return useSyncExternalStore<Consent | null | typeof UNKNOWN>(subscribe, () => (cache.raw === "__memory__" ? cache.value : read()), () => UNKNOWN);
}

export const allowed = (c: ReturnType<typeof useConsent>, cat: OptionalCategory) => c !== UNKNOWN && !!c?.[cat];

export const openConsentSettings = () => window.dispatchEvent(new Event(OPEN));
export function onOpenSettings(cb: () => void) {
  window.addEventListener(OPEN, cb);
  return () => window.removeEventListener(OPEN, cb);
}
